import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { useNavigate } from "react-router";

import ApiKeysEmptyList from "components/ApiKeys/EmptyList";
import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { ApiKey } from "types/generated";
import { AccountContext } from "views/AccountWrapper";

import { SettingsContext } from "../Context";
import { getManagementStrategy } from "../helpers";
import { GET_API_KEYS } from "./gql";
import { showApiKeysInfoDrawer } from "./InfoDrawer";
import ApiKeysListV2 from "./List";
import ApiKeysPageLayout from "./PageLayout";

const byMostRecentUsage = (x: ApiKey, y: ApiKey) => (y.lastUsedAt || 0) - (x.lastUsedAt || 0);

const REFETCH_QUERIES = [GET_API_KEYS];

const ApiKeysV2 = () => {
  const { activationStatus } = useTypedContext(SettingsContext);
  const { accountName } = useTypedContext(AccountContext);
  const navigate = useNavigate();

  const { loading, data, error } = useQuery(GET_API_KEYS);

  useTitle(`Organization Settings · API keys · ${accountName}`);

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.OrganizationApiKeys,
    defaultCallbackTrackProperties: { managementStrategy: getManagementStrategy(activationStatus) },
  });

  const memoizedApiKeys = useMemo<ApiKey[]>(() => {
    return data?.apiKeys ? [...data.apiKeys].sort(byMostRecentUsage) : [];
  }, [data]);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data) {
    return <PageLoading />;
  }

  if (!data) {
    return <NotFoundPage />;
  }

  const openInfoDrawer = () => {
    trackSegmentAnalyticsEvent("Info Click");
    showApiKeysInfoDrawer();
  };

  const openCreateApiKey = () => {
    trackSegmentAnalyticsEvent("Create Key");
    navigate("/settings/new/api-key");
  };

  return (
    <ApiKeysPageLayout
      openCreateApiKey={openCreateApiKey}
      openInfoDrawer={openInfoDrawer}
      hasApiKeys={memoizedApiKeys.length > 0}
    >
      {memoizedApiKeys.length > 0 && (
        <ApiKeysListV2
          apiKeys={memoizedApiKeys}
          refetchQueriesOnDelete={REFETCH_QUERIES}
          refetchQueriesOnUpdate={REFETCH_QUERIES}
        />
      )}
      {memoizedApiKeys.length === 0 && <ApiKeysEmptyList />}
    </ApiKeysPageLayout>
  );
};

export default ApiKeysV2;
